import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  Fab,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import KeycloakService from '../../services/KeycloakService';
import { getUserProjects } from '../../services/UserService';
import { getEmployeesInProject } from '../../services/ProjectService';
import {
  addRemoteOfficeHoursTask,
  cancelRemoteOfficeHoursQuota,
  deleteRemoteOfficeHoursTask,
  getRemoteOfficeHoursQuotaBalance,
  getRemoteOfficeHoursQuotaRequests,
  getRemoteOfficeHoursTasks,
  requestRemoteOfficeHoursQuota,
} from '../../services/RemoteOfficeHoursService';
import { getHHMMSSFromSeconds } from '../../utils/TimeConversionFunctions';
import {
  CANCEL_BUTTON_TEXT,
  ERROR_KEY,
  SHOW_NOTIFICATION,
  SUBMIT_BUTTON_TEXT,
} from '../../constants';
import PaginationComponent from '../../components/Pagination/Pagination';
import RemoteOfficeHoursQuotaCard from '../../components/RemoteOfficeHoursQuota/RemoteOfficeHoursQuotaCard';
import RemoteOfficeHoursTaskCard from '../../components/RemoteOfficeHoursTaskCard/RemoteOfficeHoursTaskCard';
import RemoteOfficeHoursTaskFilters from '../../components/RemoteOfficeHoursTaskCard/RemoteOfficeHoursTaskFilters';
import {
  APPRAISAL,
  APPROVER,
  DD_MM_YYYY,
  PROJECT,
  TASK_DESCRIPTION,
  YYYY_MM_DD,
} from '../Tasks/TaskConstants';
import { countNonWhitespaceCharacters } from '../WFH/TaskConstants';
import { getConsolidatedTimings } from '../../services/TimingService';

const MONTHLY_QUOTA_HOURS = 40;
const MONTHLY_QUOTA_SECONDS = MONTHLY_QUOTA_HOURS * 3600;
/** 4.5 hours in milliseconds — consolidated_timings.office_hrs unit. */
const MIN_OFFICE_CHECK_IN_MS = 4.5 * 60 * 60 * 1000;
const TITLE_MIN_NON_WHITESPACE_EXCLUSIVE = 30;
const DESCRIPTION_MIN_NON_WHITESPACE = 100;
const INSUFFICIENT_OFFICE_CHECK_IN_MESSAGE =
  'Remote Office Hours cannot be added because office check-in time is less than 4.5 hours for this date';
const TITLE_TOO_SHORT_MESSAGE =
  'Remote Office Hours task title must contain more than 30 non-whitespace characters';
const DESCRIPTION_TOO_SHORT_MESSAGE =
  'Remote Office Hours task description must contain at least 100 non-whitespace characters';

const hoursToSeconds = (hours) => {
  const value = Number(hours);
  if (hours === '' || hours == null || Number.isNaN(value)) {
    return 0;
  }
  return Math.round(value * 3600);
};

const secondsToHoursInput = (secs) => {
  const seconds = Number(secs);
  if (secs == null || Number.isNaN(seconds)) {
    return '';
  }
  return Number((seconds / 3600).toFixed(2));
};

const parseHoursInput = (raw) => {
  if (raw === '' || raw == null) {
    return '';
  }
  // Allow intermediate typing like "39." while editing
  if (/^\d*\.?\d*$/.test(String(raw))) {
    return raw;
  }
  return null;
};

const defaultFilters = () => ({
  startDate: moment().subtract(1, 'month').format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD'),
  projectId: '',
  status: 'PENDING',
});

const emptyFill = () => ({
  projectId: '',
  approverId: '',
  dateOfRemoteOfficeHours: moment().format(YYYY_MM_DD),
  requestedRemoteOfficeHours: '',
  title: '',
  description: '',
  isAppraisal: false,
});

const VAGUE_HOUR_CAP_MESSAGE =
  'Requested hours exceed what can be allocated for this request.';

const emptyQuota = () => ({
  projectId: '',
  quotaMonth: moment().startOf('month').format(YYYY_MM_DD),
  requestedHours: '',
  description: '',
});

const RemoteOfficeHoursComponent = () => {
  const currentUserId = KeycloakService.getUserDetail();
  const dispatch = useDispatch();
  const [tasks, setTasks] = useState([]);
  const [quotaRequests, setQuotaRequests] = useState([]);
  const [userProjects, setUserProjects] = useState([]);
  const [approverList, setApproverList] = useState([]);
  const [balance, setBalance] = useState({
    approvedQuotaSeconds: 0,
    usedFillSeconds: 0,
    remainingFillSeconds: 0,
  });
  const [fillOpen, setFillOpen] = useState(false);
  const [quotaOpen, setQuotaOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fillForm, setFillForm] = useState(emptyFill());
  const [quotaForm, setQuotaForm] = useState(emptyQuota());
  const [editId, setEditId] = useState(null);
  const [filters, setFilters] = useState(defaultFilters);
  const [page, setPage] = useState(1);
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshPage, setRefreshPage] = useState(false);
  /** 0 = Quota requests, 1 = Hours fills */
  const [sectionTab, setSectionTab] = useState(0);

  const hasActiveProjects = userProjects.length > 0;
  const remainingFill = balance.remainingFillSeconds || 0;
  const approvedQuota = balance.approvedQuotaSeconds || 0;

  const notify = (type, message) => {
    dispatch({
      type: SHOW_NOTIFICATION,
      data: { open: true, type, message },
    });
  };

  const loadBalance = useCallback(async () => {
    const response = await getRemoteOfficeHoursQuotaBalance(
      currentUserId,
      moment().format(YYYY_MM_DD),
    );
    if (!response.isError) {
      setBalance(response.data?.data || response.data || {});
    }
  }, [currentUserId]);

  const loadTasks = useCallback(async () => {
    const response = await getRemoteOfficeHoursTasks(
      currentUserId,
      filters,
      pageNo,
      pageSize,
    );
    if (!response.isError) {
      const pageData = response.data?.data || {};
      setTasks(pageData.content || []);
      setTotalPages(pageData.totalPages || 0);
    }
  }, [currentUserId, filters, pageNo, pageSize]);

  const loadQuotaRequests = useCallback(async () => {
    const month = moment().startOf('month').format(YYYY_MM_DD);
    const response = await getRemoteOfficeHoursQuotaRequests(
      currentUserId,
      { quotaMonth: month },
      0,
      20,
    );
    if (!response.isError) {
      setQuotaRequests(response.data?.data?.content || []);
    }
  }, [currentUserId]);

  const loadProjects = async () => {
    const response = await getUserProjects(currentUserId, '', '', '', 'ACTIVE');
    if (!response.isError) {
      setUserProjects(response.data.content || []);
    }
  };

  useEffect(() => {
    loadTasks();
    loadProjects();
    loadBalance();
    loadQuotaRequests();
  }, [loadTasks, loadBalance, loadQuotaRequests, refreshPage]);

  useEffect(() => {
    setPageNo(0);
    setPage(1);
    setRefreshPage((prev) => !prev);
  }, [filters]);

  const pageChange = (event, value) => {
    setPageNo(value - 1);
    setPage(value);
    setRefreshPage((prev) => !prev);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setPageNo(0);
    setPage(1);
    setRefreshPage((prev) => !prev);
  };

  const loadApproversForProject = async (projectId) => {
    const response = await getEmployeesInProject(projectId, 'ACTIVE');
    if (response.isError) {
      return [];
    }
    return (response.data.content || []).filter((item) => item.isManager);
  };

  const handleProjectChange = async (event) => {
    const projectId = event.target.value;
    setFillForm((prev) => ({ ...prev, projectId, approverId: '' }));
    const list = await loadApproversForProject(projectId);
    setApproverList(list);
  };

  const getProjectName = (projectId) => {
    const match = userProjects.find(
      (p) => String(p.projectId) === String(projectId),
    );
    return match?.projectName || '';
  };

  const getCommittedSecondsForMonth = (monthStr, excludeProjectId = null) => {
    return quotaRequests
      .filter((req) => {
        if (!moment(req.quotaMonth).isSame(moment(monthStr), 'month')) {
          return false;
        }
        if (req.status !== 'PENDING' && req.status !== 'APPROVED') {
          return false;
        }
        if (
          excludeProjectId != null &&
          String(req.projectId) === String(excludeProjectId)
        ) {
          return false;
        }
        return true;
      })
      .reduce((sum, req) => {
        if (req.status === 'APPROVED') {
          return sum + (req.approvedHours || req.requestedHours || 0);
        }
        return sum + (req.requestedHours || 0);
      }, 0);
  };

  const handleQuotaSubmit = async () => {
    if (!hasActiveProjects) {
      notify(
        ERROR_KEY,
        'Remote Office quota requires at least one active project',
      );
      return;
    }
    if (!quotaForm.projectId) {
      notify(ERROR_KEY, 'Please select a project');
      return;
    }
    const selectedMonth = moment(quotaForm.quotaMonth).startOf('month');
    if (!selectedMonth.isValid()) {
      notify(ERROR_KEY, 'Please select a valid quota month');
      return;
    }
    if (selectedMonth.isBefore(moment().startOf('month'))) {
      notify(ERROR_KEY, 'Remote Office quota cannot be requested for previous months');
      return;
    }
    const monthStr = selectedMonth.format(YYYY_MM_DD);
    const existingForProject = quotaRequests.some(
      (req) =>
        String(req.projectId) === String(quotaForm.projectId) &&
        moment(req.quotaMonth).isSame(selectedMonth, 'month') &&
        (req.status === 'PENDING' || req.status === 'APPROVED'),
    );
    if (existingForProject) {
      notify(
        ERROR_KEY,
        'A quota request for this project and month is already pending or approved.',
      );
      return;
    }
    if (quotaForm.requestedHours === '' || quotaForm.requestedHours == null) {
      notify(ERROR_KEY, 'Please enter requested quota hours');
      return;
    }
    const hours = Number(quotaForm.requestedHours);
    if (Number.isNaN(hours) || hours <= 0) {
      notify(ERROR_KEY, 'Hours must be greater than zero');
      return;
    }
    const seconds = hoursToSeconds(hours);
    const committed = getCommittedSecondsForMonth(monthStr);
    if (committed + seconds > MONTHLY_QUOTA_SECONDS) {
      notify(ERROR_KEY, VAGUE_HOUR_CAP_MESSAGE);
      return;
    }
    setLoading(true);
    const response = await requestRemoteOfficeHoursQuota({
      employeeId: currentUserId,
      projectId: quotaForm.projectId,
      quotaMonth: monthStr,
      requestedHours: seconds,
      description: quotaForm.description,
    });
    setLoading(false);
    if (!response.isError) {
      notify('success', 'Remote Office quota request submitted');
      setQuotaOpen(false);
      setQuotaForm(emptyQuota());
      setSectionTab(0);
      setRefreshPage((prev) => !prev);
    } else {
      notify(ERROR_KEY, response.message);
    }
  };

  const handleFillSubmit = async () => {
    if (
      !fillForm.projectId ||
      !fillForm.approverId ||
      !fillForm.title ||
      !fillForm.description ||
      !fillForm.requestedRemoteOfficeHours
    ) {
      notify(ERROR_KEY, 'Please fill all required fields');
      return;
    }
    if (
      countNonWhitespaceCharacters(fillForm.title) <= TITLE_MIN_NON_WHITESPACE_EXCLUSIVE
    ) {
      notify(ERROR_KEY, TITLE_TOO_SHORT_MESSAGE);
      return;
    }
    if (
      countNonWhitespaceCharacters(fillForm.description) < DESCRIPTION_MIN_NON_WHITESPACE
    ) {
      notify(ERROR_KEY, DESCRIPTION_TOO_SHORT_MESSAGE);
      return;
    }
    const hours = Number(fillForm.requestedRemoteOfficeHours);
    if (
      fillForm.requestedRemoteOfficeHours === '' ||
      fillForm.requestedRemoteOfficeHours == null ||
      Number.isNaN(hours) ||
      hours <= 0
    ) {
      notify(ERROR_KEY, 'Hours must be greater than zero');
      return;
    }
    const requestedSeconds = hoursToSeconds(hours);
    if (!editId && requestedSeconds > remainingFill) {
      notify(
        ERROR_KEY,
        `Fill exceeds approved quota remaining (${getHHMMSSFromSeconds(remainingFill)}). Request quota first.`,
      );
      return;
    }

    setLoading(true);
    const date = fillForm.dateOfRemoteOfficeHours;
    const timingResponse = await getConsolidatedTimings(currentUserId, date, date);
    if (!timingResponse.isError) {
      const timing = timingResponse.data?.content?.[0];
      const officeHrs = timing?.officeHrs;
      if (officeHrs == null || officeHrs < MIN_OFFICE_CHECK_IN_MS) {
        setLoading(false);
        notify(ERROR_KEY, INSUFFICIENT_OFFICE_CHECK_IN_MESSAGE);
        return;
      }
    }

    const payload = {
      employeeId: currentUserId,
      remoteOfficeHoursTaskDTOList: [
        {
          ...fillForm,
          id: editId,
          requestedRemoteOfficeHours: requestedSeconds,
        },
      ],
    };
    const response = await addRemoteOfficeHoursTask(payload);
    setLoading(false);
    if (!response.isError) {
      notify('success', response.message || 'Remote Office hours fill submitted');
      setFillOpen(false);
      setFillForm(emptyFill());
      setEditId(null);
      setSectionTab(1);
      setRefreshPage((prev) => !prev);
    } else {
      notify(ERROR_KEY, response.message);
    }
  };

  const handleDelete = async (task) => {
    const response = await deleteRemoteOfficeHoursTask(task.id, currentUserId);
    if (!response.isError) {
      notify('success', 'Task deleted');
      setRefreshPage((prev) => !prev);
    } else {
      notify(ERROR_KEY, response.message);
    }
  };

  const handleCancelQuota = async (request) => {
    const response = await cancelRemoteOfficeHoursQuota(request.id, currentUserId);
    if (!response.isError) {
      notify('success', 'Quota request cancelled');
      setRefreshPage((prev) => !prev);
    } else {
      notify(ERROR_KEY, response.message);
    }
  };

  const openEdit = async (task) => {
    if (task.status === 'APPROVED' || task.status === 'REJECTED') {
      return;
    }
    const list = await loadApproversForProject(task.projectId);
    setApproverList(list);
    setFillForm({
      projectId: task.projectId,
      approverId: task.approverId,
      dateOfRemoteOfficeHours: task.dateOfRemoteOfficeHours,
      requestedRemoteOfficeHours: secondsToHoursInput(task.requestedRemoteOfficeHours),
      title: task.title,
      description: task.description,
      isAppraisal: task.isAppraisal || false,
    });
    setEditId(task.id);
    setFillOpen(true);
  };

  return (
    <Box className="main-content-overlay">
      <Box className="main-content-div" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
          <Typography variant="h6">Remote Office Hours</Typography>
          <Tooltip
            title={
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Step 1: request monthly quota for a project (project PMs
                  approve; one request per project per month).
                </Typography>
                <Typography variant="body2">
                  Step 2: fill hours against approved quota (select project +
                  one manager of that project).
                </Typography>
              </>
            }
          >
            <InfoOutlinedIcon color="info" sx={{ fontSize: '18px' }} />
          </Tooltip>
        </Stack>

        <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 1 }}
          >
            This month
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            useFlexGap
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                border: '1px solid #bdbdbd',
                borderRadius: '1.5rem',
                px: '.75rem',
                py: '.3rem',
              }}
            >
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                Entitled quota
              </Typography>
              <Avatar
                sx={{
                  bgcolor: '#b35900',
                  color: 'white',
                  width: 'auto',
                  height: 20,
                  px: '.4rem',
                  fontSize: '.7rem',
                  fontWeight: 'bold',
                  borderRadius: '10px',
                }}
              >
                {getHHMMSSFromSeconds(approvedQuota)}
              </Avatar>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                border: '1px solid #bdbdbd',
                borderRadius: '1.5rem',
                px: '.75rem',
                py: '.3rem',
              }}
            >
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                Availed quota
              </Typography>
              <Avatar
                sx={{
                  bgcolor: 'grey.600',
                  color: 'white',
                  width: 'auto',
                  height: 20,
                  px: '.4rem',
                  fontSize: '.7rem',
                  fontWeight: 'bold',
                  borderRadius: '10px',
                }}
              >
                {getHHMMSSFromSeconds(balance.usedFillSeconds || 0)}
              </Avatar>
            </Stack>
          </Stack>
        </Card>

        {!hasActiveProjects && (
          <Typography sx={{ mb: 2 }} color="text.secondary">
            Remote Office Hours is unavailable because you have no active projects.
          </Typography>
        )}

        <Tabs
          value={sectionTab}
          onChange={(e, value) => setSectionTab(value)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Quota requests" />
          <Tab label="Hours fills" />
        </Tabs>

        {sectionTab === 0 && (
          <>
            {quotaRequests.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No quota requests for this month.
              </Typography>
            ) : (
              quotaRequests.map((req) => (
                <RemoteOfficeHoursQuotaCard
                  key={req.id}
                  request={req}
                  variant="employee"
                  projectName={getProjectName(req.projectId)}
                  onCancel={handleCancelQuota}
                />
              ))
            )}
          </>
        )}

        {sectionTab === 1 && (
          <>
            <RemoteOfficeHoursTaskFilters
              filters={filters}
              setFilters={setFilters}
              projects={userProjects}
            />
            {tasks.length === 0 ? (
              <Typography>No Remote Office Hours fills found.</Typography>
            ) : (
              tasks.map((task) => (
                <RemoteOfficeHoursTaskCard
                  key={task.id}
                  task={task}
                  variant="employee"
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              ))
            )}
            {tasks.length > 0 && (
              <Box className="page-holder" sx={{ mt: 2 }}>
                <PaginationComponent
                  count={totalPages}
                  page={page}
                  pageChange={pageChange}
                  pageSize={pageSize}
                  handlePageSizeChange={handlePageSizeChange}
                />
              </Box>
            )}
          </>
        )}

        {hasActiveProjects && (
          <>
            <Tooltip title="Request monthly Remote Office quota">
              <Fab
                color="secondary"
                sx={{ position: 'fixed', right: 40, bottom: 120, zIndex: 1051 }}
                onClick={() => {
                  setQuotaForm(emptyQuota());
                  setQuotaOpen(true);
                }}
              >
                <AssignmentIcon />
              </Fab>
            </Tooltip>
            {remainingFill > 0 && (
              <Tooltip title="Fill Remote Office hours against approved quota">
                <Fab
                  color="primary"
                  sx={{ position: 'fixed', right: 40, bottom: 50, zIndex: 1051 }}
                  onClick={() => {
                    setFillForm(emptyFill());
                    setEditId(null);
                    setApproverList([]);
                    setFillOpen(true);
                  }}
                >
                  <AddIcon />
                </Fab>
              </Tooltip>
            )}
          </>
        )}

        <Dialog open={quotaOpen} onClose={() => setQuotaOpen(false)} fullWidth maxWidth="sm">
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Request Remote Office Quota
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a project and requested hours for the month. One pending or approved
              request per project per month.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{PROJECT}</InputLabel>
                  <Select
                    value={quotaForm.projectId}
                    label={PROJECT}
                    onChange={(e) =>
                      setQuotaForm((prev) => ({ ...prev, projectId: e.target.value }))
                    }
                  >
                    {userProjects.map((project) => (
                      <MenuItem key={project.id} value={project.projectId}>
                        {project.projectName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Quota month"
                    views={['year', 'month']}
                    inputFormat="MMM YYYY"
                    minDate={dayjs().startOf('month')}
                    value={dayjs(quotaForm.quotaMonth)}
                    onChange={(value) => {
                      if (!value) {
                        return;
                      }
                      const monthStart = value.startOf('month');
                      if (monthStart.isBefore(dayjs().startOf('month'))) {
                        notify(
                          ERROR_KEY,
                          'Remote Office quota cannot be requested for previous months',
                        );
                        return;
                      }
                      setQuotaForm((prev) => ({
                        ...prev,
                        quotaMonth: monthStart.format(YYYY_MM_DD),
                      }));
                    }}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="text"
                  inputMode="decimal"
                  label="Requested hours"
                  placeholder="e.g. 8 or 16.5"
                  helperText="Enter hours as a number"
                  value={quotaForm.requestedHours}
                  onChange={(e) => {
                    const next = parseHoursInput(e.target.value);
                    if (next === null) {
                      return;
                    }
                    setQuotaForm((prev) => ({ ...prev, requestedHours: next }));
                  }}
                  error={
                    quotaForm.requestedHours !== '' &&
                    Number(quotaForm.requestedHours) <= 0
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Description (optional)"
                  value={quotaForm.description}
                  onChange={(e) =>
                    setQuotaForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button onClick={() => setQuotaOpen(false)}>{CANCEL_BUTTON_TEXT}</Button>
              <LoadingButton loading={loading} variant="contained" onClick={handleQuotaSubmit}>
                {SUBMIT_BUTTON_TEXT}
              </LoadingButton>
            </Box>
          </Box>
        </Dialog>

        <Dialog open={fillOpen} onClose={() => setFillOpen(false)} fullWidth maxWidth="md">
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {editId ? 'Edit Remote Office Hours Fill' : 'Fill Remote Office Hours'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Remaining approved quota: {getHHMMSSFromSeconds(remainingFill)}. Select project and
              one manager of that project.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{PROJECT}</InputLabel>
                  <Select
                    value={fillForm.projectId}
                    label={PROJECT}
                    onChange={handleProjectChange}
                    disabled={!!editId}
                  >
                    {userProjects.map((project) => (
                      <MenuItem key={project.id} value={project.projectId}>
                        {project.projectName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{APPROVER}</InputLabel>
                  <Select
                    value={fillForm.approverId}
                    label={APPROVER}
                    onChange={(e) =>
                      setFillForm((prev) => ({ ...prev, approverId: e.target.value }))
                    }
                    disabled={!!editId || !fillForm.projectId}
                  >
                    {approverList.map((manager) => (
                      <MenuItem
                        key={manager.employeeId || manager.id}
                        value={manager.employeeId || manager.id}
                      >
                        {manager.employeeName ||
                          `${manager.firstName || ''} ${manager.lastName || ''}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Date"
                    inputFormat={DD_MM_YYYY}
                    value={dayjs(fillForm.dateOfRemoteOfficeHours)}
                    maxDate={dayjs()}
                    onChange={(value) =>
                      setFillForm((prev) => ({
                        ...prev,
                        dateOfRemoteOfficeHours: value.format(YYYY_MM_DD),
                      }))
                    }
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="text"
                  inputMode="decimal"
                  label="Hours"
                  placeholder="e.g. 8 or 7.5"
                  helperText="Enter hours as a number (decimals allowed)"
                  value={fillForm.requestedRemoteOfficeHours}
                  onChange={(e) => {
                    const next = parseHoursInput(e.target.value);
                    if (next === null) {
                      return;
                    }
                    setFillForm((prev) => ({
                      ...prev,
                      requestedRemoteOfficeHours: next,
                    }));
                  }}
                  error={
                    fillForm.requestedRemoteOfficeHours !== '' &&
                    Number(fillForm.requestedRemoteOfficeHours) <= 0
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Task Title"
                  helperText="More than 30 non-whitespace characters required"
                  value={fillForm.title}
                  onChange={(e) => setFillForm((prev) => ({ ...prev, title: e.target.value }))}
                  error={
                    !!fillForm.title &&
                    countNonWhitespaceCharacters(fillForm.title) <=
                      TITLE_MIN_NON_WHITESPACE_EXCLUSIVE
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={TASK_DESCRIPTION}
                  helperText="At least 100 non-whitespace characters required"
                  value={fillForm.description}
                  onChange={(e) =>
                    setFillForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                  error={
                    !!fillForm.description &&
                    countNonWhitespaceCharacters(fillForm.description) <
                      DESCRIPTION_MIN_NON_WHITESPACE
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={fillForm.isAppraisal}
                      onChange={(e) =>
                        setFillForm((prev) => ({ ...prev, isAppraisal: e.target.checked }))
                      }
                    />
                  }
                  label={APPRAISAL}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button onClick={() => setFillOpen(false)}>{CANCEL_BUTTON_TEXT}</Button>
              <LoadingButton loading={loading} variant="contained" onClick={handleFillSubmit}>
                {SUBMIT_BUTTON_TEXT}
              </LoadingButton>
            </Box>
          </Box>
        </Dialog>
      </Box>
    </Box>
  );
};

export default RemoteOfficeHoursComponent;
